<template>
  <basic-page title="Ajoneuvotiedot">
    <v-row class="fill-height">
      <v-col cols="12" lg="8" offset-lg="2" xl="6" offset-xl="3">
        <v-card>
          <v-card-title
            class="d-flex align-center justify-space-between flex-wrap py-2"
          >
            <span>Hae ajoneuvon tiedot rekisteritunnuksella</span>
            <v-btn
              text
              small
              color="primary"
              class="font-weight-bold my-1"
              @click="showHistoryDialog = true"
            >
              <v-icon left small>mdi-history</v-icon>
              Hakuhistoria
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-form @submit.prevent="haeAjoneuvo">
              <v-row align="center">
                <v-col cols="12" sm="8" class="d-flex align-center">
                  <v-text-field
                    v-model="regNumber"
                    label="Rekisteritunnus"
                    placeholder="ABC-123"
                    outlined
                    dense
                    hide-details
                    clearable
                    :disabled="loading || ocrLoading"
                    @click:clear="resetSearch"
                  />
                  <input
                    ref="plateCameraInput"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style="display: none"
                    @change="onPlateImageCaptured"
                  />
                  <v-btn
                    icon
                    large
                    color="primary"
                    class="ml-2"
                    :loading="ocrLoading"
                    title="Ota kuva rekisterikilvestä kameralla"
                    @click="$refs.plateCameraInput.click()"
                  >
                    <v-icon>mdi-camera</v-icon>
                  </v-btn>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-btn
                    color="primary"
                    block
                    :loading="loading"
                    :disabled="!regNumber || ocrLoading"
                    type="submit"
                  >
                    <v-icon left>mdi-magnify</v-icon>
                    Hae
                  </v-btn>
                </v-col>
              </v-row>
              <div
                v-if="ocrLoading"
                class="d-flex align-center mt-2 grey--text text-caption"
              >
                <v-progress-circular
                  indeterminate
                  size="16"
                  width="2"
                  color="primary"
                  class="mr-2"
                />
                Gemini 3.5 Flash tunnistaa rekisterikilpeä kuvasta...
              </div>
              <div
                v-if="recentHistory.length > 0 && !regNumber"
                class="mt-2 d-flex align-center flex-wrap"
              >
                <span class="caption grey--text mr-2">Viimeisimmät haut:</span>
                <v-chip
                  v-for="item in recentHistory"
                  :key="item.regNumber"
                  small
                  outlined
                  class="mr-1 mb-1 font-weight-bold"
                  color="primary"
                  @click="onSelectPlate(item.regNumber)"
                >
                  <v-icon left x-small>mdi-history</v-icon>
                  {{ item.regNumber }}
                </v-chip>
              </div>
            </v-form>
          </v-card-text>
        </v-card>

        <pelastuskortti-selain v-if="!regNumber" class="mt-4" />

        <v-card v-if="vehicle" class="mt-4">
          <v-card-title class="d-flex align-center">
            <span>{{ vehicle.title }}</span>
            <v-icon
              v-if="isElectricOrHybrid"
              color="amber darken-2"
              size="48"
              class="ml-2"
              title="Sähkö-/hybridiajoneuvo"
            >
              mdi-flash
            </v-icon>
          </v-card-title>
          <v-card-subtitle v-if="vehicle.description">{{
            vehicle.description
          }}</v-card-subtitle>
          <v-card-text>
            <v-simple-table dense>
              <tbody>
                <tr>
                  <td class="font-weight-bold">Merkki</td>
                  <td>{{ vehicle.manufacturer }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Malli</td>
                  <td>{{ vehicle.modelName }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">Vuosimalli</td>
                  <td>{{ vehicle.modelYear }}</td>
                </tr>
                <tr v-if="vehicle.registrationDate">
                  <td class="font-weight-bold">Rekisteröintipäivämäärä</td>
                  <td>{{ vehicle.registrationDate }}</td>
                </tr>
                <tr v-if="vehicle.vin">
                  <td class="font-weight-bold">VIN-numero</td>
                  <td>{{ vehicle.vin }}</td>
                </tr>
                <tr v-if="vehicle.gearbox">
                  <td class="font-weight-bold">Vaihdelaatikko</td>
                  <td>{{ vehicle.gearbox.gearbox }}</td>
                </tr>
                <tr v-if="vehicle.fuel">
                  <td class="font-weight-bold">Polttoaine</td>
                  <td>{{ vehicle.fuel.fuel }}</td>
                </tr>
                <tr
                  v-if="vehicle.engine && vehicle.engine.engingeSpecification"
                >
                  <td class="font-weight-bold">Teho</td>
                  <td>
                    {{ vehicle.engine.engingeSpecification.powerHp }} hv ({{
                      vehicle.engine.engingeSpecification.powerKW
                    }}
                    kW)
                  </td>
                </tr>
                <tr v-if="vehicle.body && vehicle.body.body">
                  <td class="font-weight-bold">Kori</td>
                  <td>
                    {{ vehicle.body.body.body
                    }}<template v-if="vehicle.body.door">
                      , {{ vehicle.body.door.doorCount }} ovea</template
                    >
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>

        <v-card v-if="vehicle" class="mt-4">
          <v-card-title class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-car-battery</v-icon>
              <span>Akkujen sijainnit (Tekoälyanalyysi)</span>
            </div>
            <v-btn
              icon
              small
              :loading="batteryLoading"
              title="Analysoi uudelleen"
              @click="haeBatteryAnalysis"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div v-if="batteryLoading" class="text-center py-4">
              <v-progress-circular indeterminate color="primary" class="mb-2" />
              <div class="text-caption grey--text">
                Gemini analysoi akkujen sijainteja ja katkaisupisteitä...
              </div>
            </div>

            <div v-else-if="batteryError" class="py-2">
              <v-alert type="warning" dense text class="mb-2">
                {{ batteryError }}
              </v-alert>
              <v-btn small color="primary" outlined @click="haeBatteryAnalysis">
                <v-icon left small>mdi-refresh</v-icon>
                Yritä uudelleen
              </v-btn>
            </div>

            <div v-else-if="batteryAnalysis">
              <v-alert
                type="info"
                dense
                text
                prominent
                icon="mdi-robot"
                class="mb-4"
              >
                <div class="font-weight-medium">Tekoälyn arvio (Gemini)</div>
                <div class="text-caption">
                  Tämä analyysi on tekoälyn luoma suuntaa-antava arvio ajoneuvon
                  tietojen perusteella. Sijainnit ja varusteet voivat poiketa
                  todellisesta yksilöstä tai vuosimallista. Tarkista aina
                  ensisijaisesti virallinen pelastuskortti (Rescue Sheet) ja
                  noudata pelastusviranomaisen turvaohjeita.
                </div>
              </v-alert>

              <v-row dense>
                <!-- 12V / 16V Low voltage battery -->
                <v-col cols="12" :md="batteryAnalysis.isHighVoltage ? 6 : 12">
                  <v-card outlined class="pa-3 fill-height battery-box">
                    <div class="d-flex align-center mb-2">
                      <v-icon color="blue-grey darken-2" class="mr-2">
                        mdi-car-battery
                      </v-icon>
                      <span class="font-weight-bold text-subtitle-2">
                        Pienjänniteakku ({{
                          (batteryAnalysis.lowVoltageBattery &&
                            batteryAnalysis.lowVoltageBattery.batteryType) ||
                          "12V"
                        }})
                      </span>
                      <v-chip
                        v-if="
                          batteryAnalysis.lowVoltageBattery &&
                          batteryAnalysis.lowVoltageBattery.multipleBatteries
                        "
                        x-small
                        color="amber darken-3"
                        text-color="white"
                        class="ml-2"
                      >
                        Useampi akku
                      </v-chip>
                    </div>

                    <div class="text-body-2 mb-1">
                      <span class="font-weight-bold">Sijainti:</span>
                      {{
                        (batteryAnalysis.lowVoltageBattery &&
                          batteryAnalysis.lowVoltageBattery.location) ||
                        "Tieto ei saatavilla"
                      }}
                    </div>

                    <div
                      v-if="
                        batteryAnalysis.lowVoltageBattery &&
                        batteryAnalysis.lowVoltageBattery.accessNotes
                      "
                      class="text-body-2 grey--text text--darken-2"
                    >
                      <span class="font-weight-bold">Pääsy / apuvirta:</span>
                      {{ batteryAnalysis.lowVoltageBattery.accessNotes }}
                    </div>
                  </v-card>
                </v-col>

                <!-- High voltage battery (if electric/hybrid) -->
                <v-col
                  v-if="batteryAnalysis.isHighVoltage || isElectricOrHybrid"
                  cols="12"
                  md="6"
                >
                  <v-card
                    outlined
                    class="pa-3 fill-height battery-box battery-box--high-voltage"
                  >
                    <div class="d-flex align-center mb-2">
                      <v-icon color="amber darken-2" class="mr-2">
                        mdi-flash-alert
                      </v-icon>
                      <span class="font-weight-bold text-subtitle-2">
                        Korkeajänniteakku (HV)
                      </span>
                    </div>

                    <div
                      v-if="
                        batteryAnalysis.highVoltageBattery &&
                        batteryAnalysis.highVoltageBattery.location
                      "
                      class="text-body-2 mb-1"
                    >
                      <span class="font-weight-bold">Akun sijainti:</span>
                      {{ batteryAnalysis.highVoltageBattery.location }}
                    </div>

                    <div
                      v-if="
                        batteryAnalysis.highVoltageBattery &&
                        batteryAnalysis.highVoltageBattery.disconnectLocations
                      "
                      class="text-body-2 mb-1"
                    >
                      <span class="font-weight-bold">Katkaisukohdat:</span>
                      {{
                        batteryAnalysis.highVoltageBattery.disconnectLocations
                      }}
                    </div>

                    <div
                      v-if="
                        batteryAnalysis.highVoltageBattery &&
                        batteryAnalysis.highVoltageBattery.safetyNotes
                      "
                      class="text-caption red--text text--darken-3 mt-1"
                    >
                      <v-icon x-small color="red darken-3" class="mr-1">
                        mdi-alert
                      </v-icon>
                      {{ batteryAnalysis.highVoltageBattery.safetyNotes }}
                    </div>
                  </v-card>
                </v-col>

                <!-- General notes if available -->
                <v-col
                  v-if="batteryAnalysis.generalNotes"
                  cols="12"
                  class="mt-1"
                >
                  <div class="text-caption grey--text text--darken-1">
                    <span class="font-weight-bold">Huomiot:</span>
                    {{ batteryAnalysis.generalNotes }}
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-card>

        <v-card v-if="rescueLoading || rescueVariants.length" class="mt-4">
          <v-card-title>Pelastuskortti</v-card-title>
          <v-card-text>
            <div v-if="rescueLoading" class="text-center py-4">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <template v-else>
              <v-alert v-if="!selectedVariant" type="info" text dense>
                Sopivaa pelastuskorttia ei löytynyt.
              </v-alert>
              <v-card v-else outlined class="pa-3 mb-4">
                <v-row align="center">
                  <v-col cols="12" sm="4" class="text-center">
                    <div
                      v-if="
                        selectedVariant.pictureUrl &&
                        !proxiedImages[selectedVariant.id] &&
                        !brokenImages[selectedVariant.id]
                      "
                      class="d-flex align-center justify-center"
                      style="height: 220px"
                    >
                      <v-progress-circular indeterminate color="primary" />
                    </div>
                    <v-img
                      v-else-if="proxiedImages[selectedVariant.id]"
                      :src="proxiedImages[selectedVariant.id]"
                      max-height="220"
                      contain
                    />
                    <div
                      v-else
                      class="d-flex align-center justify-center"
                      style="height: 220px"
                    >
                      <v-icon size="72" color="grey lighten-1"
                        >mdi-car-side</v-icon
                      >
                    </div>
                  </v-col>
                  <v-col cols="12" sm="5">
                    <div class="font-weight-bold">
                      {{ selectedVariant.name || selectedVariant.modelName }}
                      <v-chip
                        v-if="selectedVariant.id === suggestedId"
                        x-small
                        color="primary"
                        text-color="white"
                        class="ml-2"
                      >
                        Suositus
                      </v-chip>
                    </div>
                    <div class="text-body-1">
                      {{ selectedVariant.bodyType }} ·
                      {{ selectedVariant.buildYearFrom }}–{{
                        selectedVariant.buildYearUntil || ""
                      }}
                      <template v-if="selectedVariant.doors">
                        · {{ selectedVariant.doors }} ovea</template
                      >
                    </div>
                  </v-col>
                  <v-col cols="12" sm="3" class="text-center text-sm-right">
                    <v-btn
                      v-if="selectedVariant.rescueSheetUrl"
                      color="primary"
                      @click="openRescueSheet(selectedVariant)"
                    >
                      <v-icon left>mdi-file-pdf-box</v-icon>
                      Avaa pelastuskortti
                    </v-btn>
                    <div v-else class="text-caption">
                      Ei pelastuskorttia saatavilla
                    </div>
                  </v-col>
                </v-row>
              </v-card>

              <v-breadcrumbs
                v-if="allYearsMode"
                class="pl-0"
                style="flex-wrap: wrap"
              >
                <v-breadcrumbs-item @click.native="allYearsMode = false">
                  {{ vehicle.manufacturer }}
                </v-breadcrumbs-item>
                <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
                <v-breadcrumbs-item disabled>
                  {{ matchedModelName }}
                </v-breadcrumbs-item>
              </v-breadcrumbs>

              <div v-if="!allYearsMode && rescueVariants.length > 1">
                <div class="text-subtitle-2 mb-2">
                  Vaihtoehtoiset mallit / vuosimallit
                </div>
                <v-row>
                  <v-col
                    v-for="v in rescueVariants"
                    :key="v.id"
                    cols="12"
                    sm="6"
                    md="4"
                  >
                    <v-card
                      outlined
                      class="variant-card"
                      :class="{
                        'variant-card--selected': v.id === selectedVariantId,
                      }"
                      @click="selectedVariantId = v.id"
                    >
                      <div
                        v-if="
                          v.pictureUrl &&
                          !proxiedImages[v.id] &&
                          !brokenImages[v.id]
                        "
                        class="d-flex align-center justify-center"
                        style="height: 180px"
                      >
                        <v-progress-circular indeterminate color="primary" />
                      </div>
                      <v-img
                        v-else-if="proxiedImages[v.id]"
                        :src="proxiedImages[v.id]"
                        height="180"
                        contain
                      />
                      <div
                        v-else
                        class="d-flex align-center justify-center"
                        style="height: 180px"
                      >
                        <v-icon size="64" color="grey lighten-1"
                          >mdi-car-side</v-icon
                        >
                      </div>
                      <v-card-text class="pa-2 text-center">
                        <div class="text-body-2 font-weight-bold">
                          {{ v.name || v.modelName }}
                        </div>
                        <div class="text-body-2">
                          {{ v.bodyType }} · {{ v.buildYearFrom }}–{{
                            v.buildYearUntil || ""
                          }}
                        </div>
                        <v-chip
                          v-if="v.id === suggestedId"
                          x-small
                          color="primary"
                          text-color="white"
                          class="mt-1"
                        >
                          Suositus
                        </v-chip>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <v-btn
                  text
                  small
                  color="primary"
                  class="mt-2"
                  :loading="allYearsLoading"
                  @click="showAllModelYears"
                >
                  <v-icon left small>mdi-calendar-search</v-icon>
                  Näytä kaikki {{ vehicle.manufacturer }}
                  {{ matchedModelName }} -mallivuodet
                </v-btn>
              </div>

              <div v-if="allYearsMode">
                <div v-if="allYearsLoading" class="text-center py-4">
                  <v-progress-circular indeterminate color="primary" />
                </div>
                <v-row v-else>
                  <v-col
                    v-for="v in allVariants"
                    :key="v.id"
                    cols="12"
                    sm="6"
                    md="4"
                  >
                    <v-card
                      outlined
                      class="variant-card"
                      :class="{
                        'variant-card--selected': v.id === selectedVariantId,
                      }"
                      @click="selectedVariantId = v.id"
                    >
                      <div
                        v-if="
                          v.pictureUrl &&
                          !proxiedImages[v.id] &&
                          !brokenImages[v.id]
                        "
                        class="d-flex align-center justify-center"
                        style="height: 180px"
                      >
                        <v-progress-circular indeterminate color="primary" />
                      </div>
                      <v-img
                        v-else-if="proxiedImages[v.id]"
                        :src="proxiedImages[v.id]"
                        height="180"
                        contain
                      />
                      <div
                        v-else
                        class="d-flex align-center justify-center"
                        style="height: 180px"
                      >
                        <v-icon size="64" color="grey lighten-1"
                          >mdi-car-side</v-icon
                        >
                      </div>
                      <v-card-text class="pa-2 text-center">
                        <div class="text-body-2 font-weight-bold">
                          {{ v.name || v.modelName }}
                        </div>
                        <div class="text-body-2">
                          {{ v.bodyType }} · {{ v.buildYearFrom }}–{{
                            v.buildYearUntil || ""
                          }}
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
                <div
                  v-if="!allYearsLoading && allVariants.length === 0"
                  class="text-center grey--text py-4"
                >
                  Malleja ei löytynyt
                </div>
              </div>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <pelastuskortti-dialog
      v-model="viewerDialog"
      :url="viewerUrl"
      :title="viewerTitle"
    />

    <plate-search-history-dialog
      v-model="showHistoryDialog"
      @select="onSelectPlate"
    />
  </basic-page>
</template>

<script>
import BasicPage from "@/components/BasicPage";
import PelastuskorttiSelain from "@/components/PelastuskorttiSelain";
import PelastuskorttiDialog from "@/components/PelastuskorttiDialog";
import PlateSearchHistoryDialog from "@/components/PlateSearchHistoryDialog";
import {
  getPlateSearchHistory,
  addPlateSearchHistory,
  updatePlateSearchVehicleInfo,
} from "@/utils/plateSearchHistory";
import {
  searchRescueSheets,
  fetchRescueVariantsByModel,
} from "@/utils/pelastuskortitFirestore";
import api from "@/api";

export default {
  name: "Ajoneuvotiedot",
  components: {
    BasicPage,
    PelastuskorttiSelain,
    PelastuskorttiDialog,
    PlateSearchHistoryDialog,
  },
  data: () => ({
    viewerDialog: false,
    viewerUrl: "",
    viewerTitle: "",
    regNumber: "",
    loading: false,
    vehicle: null,
    rescueLoading: false,
    rescueVariants: [],
    suggestedId: null,
    selectedVariantId: null,
    brokenImages: {},
    proxiedImages: {},
    allYearsMode: false,
    allYearsLoading: false,
    allVariants: [],
    batteryLoading: false,
    batteryAnalysis: null,
    batteryError: null,
    ocrLoading: false,
    showHistoryDialog: false,
    recentHistory: [],
  }),
  mounted() {
    this.loadRecentHistory();
    if (this.$route.query.rekisterinumero || this.$route.query.regNumber) {
      const reg = (
        this.$route.query.rekisterinumero || this.$route.query.regNumber
      ).trim();
      if (reg) {
        this.regNumber = reg;
        this.haeAjoneuvo();
      }
    }
  },
  watch: {
    "$route.query": {
      handler(newQuery) {
        const reg = (
          newQuery?.rekisterinumero ||
          newQuery?.regNumber ||
          ""
        ).trim();
        if (reg && reg !== this.regNumber) {
          this.regNumber = reg;
          this.haeAjoneuvo();
        }
      },
    },
  },
  computed: {
    selectedVariant() {
      return [...this.rescueVariants, ...this.allVariants].find(
        (v) => v.id === this.selectedVariantId,
      );
    },
    matchedModelName() {
      const reference = this.selectedVariant || this.rescueVariants[0];
      return (
        (reference && reference.modelName) ||
        (this.vehicle && this.vehicle.modelName)
      );
    },
    isElectricOrHybrid() {
      const fuel =
        (this.vehicle && this.vehicle.fuel && this.vehicle.fuel.fuel) || "";
      return /sähkö|hybridi|hybrid|plug-?in|electric|phev|bev|hev/i.test(fuel);
    },
  },
  methods: {
    loadRecentHistory() {
      const hist = getPlateSearchHistory();
      this.recentHistory = hist.slice(0, 5);
    },
    onSelectPlate(reg) {
      this.regNumber = reg;
      this.haeAjoneuvo();
    },
    resetSearch() {
      this.regNumber = "";
      this.vehicle = null;
      this.rescueVariants = [];
      this.suggestedId = null;
      this.selectedVariantId = null;
      this.allYearsMode = false;
      this.allVariants = [];
      this.proxiedImages = {};
      this.brokenImages = {};
      this.batteryLoading = false;
      this.batteryAnalysis = null;
      this.batteryError = null;
      this.loadRecentHistory();
    },
    async haeAjoneuvo() {
      if (!this.regNumber) return;
      this.loading = true;
      this.vehicle = null;
      this.rescueVariants = [];
      this.suggestedId = null;
      this.selectedVariantId = null;
      this.allYearsMode = false;
      this.allVariants = [];
      this.proxiedImages = {};
      this.brokenImages = {};
      this.batteryLoading = false;
      this.batteryAnalysis = null;
      this.batteryError = null;

      addPlateSearchHistory(this.regNumber);
      this.loadRecentHistory();

      try {
        const result = (
          await api.vehicleInfoByRegNumber({ regNumber: this.regNumber })
        ).data;
        this.vehicle = result;

        const vehicleTitle = [
          this.vehicle.manufacturer,
          this.vehicle.modelName,
          this.vehicle.modelYear ? `(${this.vehicle.modelYear})` : "",
        ]
          .filter(Boolean)
          .join(" ");
        if (vehicleTitle) {
          updatePlateSearchVehicleInfo(this.regNumber, vehicleTitle);
          this.loadRecentHistory();
        }

        this.haeRescueSheet();
        this.haeBatteryAnalysis();
      } catch (e) {
        this.$toast.error(e.message || "Ajoneuvon tietojen haku epäonnistui");
      } finally {
        this.loading = false;
      }
    },
    async haeBatteryAnalysis() {
      if (!this.vehicle || !this.vehicle.manufacturer) return;
      this.batteryLoading = true;
      this.batteryError = null;
      try {
        const response = (
          await api.vehicleBatteryLocationAnalysis({
            manufacturer: this.vehicle.manufacturer,
            modelName: this.vehicle.modelName,
            modelYear: this.vehicle.modelYear,
            fuel: this.vehicle.fuel && this.vehicle.fuel.fuel,
            body:
              this.vehicle.body &&
              this.vehicle.body.body &&
              this.vehicle.body.body.body,
            engine:
              this.vehicle.engine &&
              this.vehicle.engine.engingeSpecification &&
              `${this.vehicle.engine.engingeSpecification.powerHp || ""} hv`,
            variantName:
              this.selectedVariant &&
              (this.selectedVariant.name || this.selectedVariant.modelName),
          })
        ).data;

        if (response.success && response.analysis) {
          this.batteryAnalysis = response.analysis;
        } else {
          this.batteryError =
            response.error || "Akkujen analyysin haku epäonnistui";
        }
      } catch (e) {
        this.batteryError = e.message || "Akkujen analyysin haku epäonnistui";
      } finally {
        this.batteryLoading = false;
      }
    },
    async haeRescueSheet() {
      if (!this.vehicle || !this.vehicle.manufacturer) return;
      this.rescueLoading = true;
      try {
        const bodyType =
          this.vehicle.body &&
          this.vehicle.body.body &&
          this.vehicle.body.body.body;

        // Direct frontend Firestore query with client-side ranking
        let result = await searchRescueSheets({
          make: this.vehicle.manufacturer,
          model: this.vehicle.modelName,
          year: this.vehicle.modelYear,
          bodyType,
        });

        // If no variants found from client query, try fallback to backend callable
        if (!result.variants || result.variants.length === 0) {
          try {
            const backendRes = (
              await api.rescueSheetSearch({
                make: this.vehicle.manufacturer,
                model: this.vehicle.modelName,
                year: this.vehicle.modelYear,
                bodyType,
              })
            ).data;
            if (backendRes && backendRes.variants && backendRes.variants.length) {
              result = backendRes;
            }
          } catch (fbErr) {
            // Keep original result if backend also fails
          }
        }

        this.rescueVariants = result.variants || [];
        this.suggestedId = result.suggestedId;
        this.selectedVariantId = result.suggestedId;
        this.loadVariantImages(this.rescueVariants);
      } catch (e) {
        // Fallback to backend cloud function
        try {
          const result = (
            await api.rescueSheetSearch({
              make: this.vehicle.manufacturer,
              model: this.vehicle.modelName,
              year: this.vehicle.modelYear,
              bodyType:
                this.vehicle.body &&
                this.vehicle.body.body &&
                this.vehicle.body.body.body,
            })
          ).data;
          this.rescueVariants = result.variants || [];
          this.suggestedId = result.suggestedId;
          this.selectedVariantId = result.suggestedId;
          this.loadVariantImages(this.rescueVariants);
        } catch (fbErr) {
          this.$toast.error(fbErr.message || "Pelastuskorttien haku epäonnistui");
        }
      } finally {
        this.rescueLoading = false;
      }
    },
    async showAllModelYears() {
      // Use the already-matched EuroNCAP model name (v.modelName), not vehicle.modelName from
      // Biltema — the two naming conventions don't always match exactly and rescueDatabaseVariants
      // requires an exact match.
      if (!this.vehicle || !this.matchedModelName) return;
      this.allYearsMode = true;
      this.allYearsLoading = true;
      try {
        const { variants } = await fetchRescueVariantsByModel(
          this.vehicle.manufacturer,
          this.matchedModelName,
        );
        this.allVariants = variants;
        this.loadVariantImages(this.allVariants);
      } catch (e) {
        try {
          const result = (
            await api.rescueDatabaseVariants({
              make: this.vehicle.manufacturer,
              model: this.matchedModelName,
            })
          ).data;
          this.allVariants = result.variants;
          this.loadVariantImages(this.allVariants);
        } catch (fbErr) {
          this.$toast.error(fbErr.message || "Mallivuosien haku epäonnistui");
        }
      } finally {
        this.allYearsLoading = false;
      }
    },
    loadVariantImages(variants) {
      (variants || []).forEach((v) => {
        if (
          v &&
          v.pictureUrl &&
          !this.proxiedImages[v.id] &&
          !this.brokenImages[v.id]
        ) {
          this.fetchVariantImage(v);
        }
      });
    },
    async fetchVariantImage(variant) {
      if (!variant?.pictureUrl) return;
      if (this.proxiedImages[variant.id]) return;
      try {
        const result = (await api.fetchImage({ url: variant.pictureUrl })).data;
        this.$set(
          this.proxiedImages,
          variant.id,
          `data:${result.mimeType};base64,${result.base64}`,
        );
      } catch (e) {
        this.$set(this.brokenImages, variant.id, true);
      }
    },
    async onPlateImageCaptured(event) {
      const file = event?.target?.files?.[0];
      if (!file) return;

      this.ocrLoading = true;
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = reader.result;
            const b64 = String(dataUrl).split(",")[1];
            resolve(b64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const mimeType = file.type || "image/jpeg";
        const result = (
          await api.recognizePlateNumber({
            imageBase64: base64,
            mimeType,
          })
        ).data;

        if (result.success && result.plateNumber) {
          this.regNumber = result.plateNumber;
          this.$toast.success(`Kilpi tunnistettu: ${result.plateNumber}`);
          this.haeAjoneuvo();
        } else {
          this.$toast.warning(
            result.error ||
              "Rekisterikilpeä ei pystytty tunnistamaan kuvasta. Syötä tunnus käsin.",
          );
        }
      } catch (e) {
        this.$toast.error(
          e.message || "Rekisterikilven kuvan käsittely epäonnistui",
        );
      } finally {
        this.ocrLoading = false;
        if (event?.target) event.target.value = "";
      }
    },
    openRescueSheet(v) {
      if (!v || !v.rescueSheetUrl) return;
      const make = this.vehicle?.manufacturer || v.make_name || "";
      const model = v.modelName || v.name || this.vehicle?.modelName || "";
      const years = [v.buildYearFrom, v.buildYearUntil]
        .filter(Boolean)
        .join("–");
      const titleParts = [make, model, years ? `(${years})` : ""]
        .filter(Boolean)
        .join(" ");
      this.viewerTitle = `Pelastuskortti: ${titleParts || "Ajoneuvo"}`;
      this.viewerUrl = v.rescueSheetUrl;
      this.viewerDialog = true;
    },
  },
};
</script>

<style scoped lang="scss">
.variant-card {
  cursor: pointer;
  height: 100%;

  &--selected {
    border-color: #6c0000;
    border-width: 2px;
  }
}

.battery-box {
  background-color: #fafafa;
  border-radius: 8px;

  &--high-voltage {
    background-color: #fffdf5;
    border-color: #ffb400;
  }
}
</style>
